/**
 * AiEnrichArticleModal — Phase 4e.C/D (Feb 2026)
 *
 * Diff-Modal für KI-Nachbearbeitung eines WMS-Artikels.
 * Workflow:
 *   1) "KI recherchieren" → ruft Backend, zeigt pro Feld Original ↔ Vorschlag mit Checkbox
 *   2) "Bilder neu rendern (Nano Banana)" → zeigt Referenzbild + bis 6 KI-Varianten
 *      mit Before/After-Side-by-Side, jeder einzelnd auswählbar
 *   3) "Übernehmen" → POST /apply mit selektierten Feldern + Bildpfaden
 *
 * Plagiatschutz: KI-Bilder werden via Gemini Nano Banana komplett neu gerendert
 * (kein Stempel/Crop — eigenständige Renditions, sichtbar mit ✨ KI-Badge).
 */
import { useEffect, useState } from "react";
import { X, Sparkles, Loader2, Check, ImagePlus, ExternalLink, Globe } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import PhotoThumb from "./PhotoThumb";

export default function AiEnrichArticleModal({ article, onClose, onApplied }) {
    const [stage, setStage] = useState("idle"); // idle | researching | research_done | regen | regen_done | applying
    const [diff, setDiff] = useState([]);
    const [extras, setExtras] = useState({});
    const [sources, setSources] = useState([]);
    const [acceptField, setAcceptField] = useState({}); // {field: true/false}
    const [proposals, setProposals] = useState([]); // [{path, ...}]
    const [referencePath, setReferencePath] = useState(null);
    const [acceptImages, setAcceptImages] = useState({}); // {path: true/false}
    const [replaceImages, setReplaceImages] = useState(false);
    const [regenCount, setRegenCount] = useState(3);
    // Phase 4e.E — Sichtbarkeit + Split-Preise direkt im Modal
    const [vis, setVis] = useState({
        visible_b2c: !!article.visible_b2c,
        visible_b2b: !!article.visible_b2b,
        internal_only: article.internal_only !== false,
        price_b2c_gross: article.price_b2c_gross ?? "",
        price_b2b_net: article.price_b2b_net ?? "",
    });
    const [savingVis, setSavingVis] = useState(false);
    // Jul-2026 (User-Wunsch) — Zusatz-Infos (Materialien/Tags/Kompatibilität) am Artikel speichern
    const [saveExtras, setSaveExtras] = useState(true);
    const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
    // Phase 4e.G++ — Storefront-Vorschau-Tab
    const [showStorefront, setShowStorefront] = useState(false);
    const previewPrice = vis.price_b2c_gross || article.price_b2c_gross || article.sell_price_gross || 0;
    const inStock = (article.stock_qty || 0) > 0;

    // Phase 4e.G — Auto-Load pending Batch-Proposal beim Öffnen
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/articles/${article.id}/ai-enrich/proposals`);
                if (cancelled || !r.data?.has_proposal) return;
                setDiff(r.data.diff || []);
                setExtras(r.data.extras || {});
                setSources(r.data.sources || []);
                const initial = {};
                (r.data.diff || []).forEach((d) => { initial[d.field] = true; });
                setAcceptField(initial);
                setStage("research_done");
            } catch (_e) { /* no proposal — ignore */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [article.id]);

    // Phase 4e.G+ — Tastenkürzel (Linear/Superhuman-Stil)
    //   Esc → schließen
    //   A   → alle Vorschläge akzeptieren
    //   R   → alle Vorschläge ablehnen
    //   Enter → Auswahl übernehmen (wenn `research_done` oder `regen_done`)
    useEffect(() => {
        const handler = (e) => {
            // Ignoriere, wenn Fokus in einem Input/Textarea ist
            const tag = (e.target?.tagName || "").toLowerCase();
            if (tag === "input" || tag === "textarea" || tag === "select") return;
            if (e.key === "Escape") {
                onClose?.();
            } else if (e.key === "a" || e.key === "A") {
                const next = {};
                diff.forEach((d) => { next[d.field] = true; });
                setAcceptField(next);
                e.preventDefault();
            } else if (e.key === "r" || e.key === "R") {
                const next = {};
                diff.forEach((d) => { next[d.field] = false; });
                setAcceptField(next);
                e.preventDefault();
            } else if (e.key === "Enter" && (stage === "research_done" || stage === "regen_done")) {
                apply();
                e.preventDefault();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [diff, stage]);

    const saveVisibility = async () => {
        setSavingVis(true);
        try {
            const payload = {
                visible_b2c: vis.visible_b2c,
                visible_b2b: vis.visible_b2b,
                internal_only: vis.internal_only,
            };
            const p1 = String(vis.price_b2c_gross).trim();
            const p2 = String(vis.price_b2b_net).trim();
            if (p1 !== "") payload.price_b2c_gross = Number(p1);
            if (p2 !== "") payload.price_b2b_net = Number(p2);
            const r = await api.patch(`/articles/${article.id}`, payload);
            toast.success("Sichtbarkeit & Preise gespeichert");
            onApplied?.(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        }
        setSavingVis(false);
    };

    const startResearch = async () => {
        setStage("researching");
        try {
            const r = await api.post(`/articles/${article.id}/ai-enrich/research`);
            setDiff(r.data.diff || []);
            setExtras(r.data.extras || {});
            setSources(r.data.sources || []);
            const initial = {};
            (r.data.diff || []).forEach((d) => { initial[d.field] = true; });
            setAcceptField(initial);
            setStage("research_done");
            if (!(r.data.diff || []).length) {
                toast.info("KI hat keine neuen Vorschläge gefunden — Artikel scheint bereits vollständig.");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "KI-Recherche fehlgeschlagen");
            setStage("idle");
        }
    };

    const startRegen = async () => {
        setStage("regen");
        try {
            const r = await api.post(`/articles/${article.id}/ai-enrich/regen-images`, { count: regenCount });
            setProposals(r.data.proposals || []);
            setReferencePath(r.data.reference_path);
            const initial = {};
            (r.data.proposals || []).forEach((p) => { initial[p.path] = false; });
            setAcceptImages(initial);
            setStage("regen_done");
            if (!(r.data.proposals || []).length) {
                toast.error("KI konnte keine Bilder generieren.");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bild-Rendering fehlgeschlagen");
            setStage("research_done");
        }
    };

    // Jul-2026 (User-Wunsch) — passende echte Produktbilder im Netz suchen.
    const startWebImages = async () => {
        setStage("regen");
        try {
            const r = await api.post(`/articles/${article.id}/ai-enrich/web-images`);
            const props = r.data.proposals || [];
            setProposals(props);
            setReferencePath((article.photo_paths || [])[0] || null);
            const initial = {};
            props.forEach((p) => { initial[p.path] = false; });
            setAcceptImages(initial);
            setStage("regen_done");
            if (!props.length) {
                toast.info("Keine verwendbaren Produktbilder im Netz gefunden — probiere das KI-Rendering.");
            } else {
                toast.success(`${props.length} Produktbild(er) im Netz gefunden — bitte Nutzungsrechte prüfen`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Web-Bildersuche fehlgeschlagen");
            setStage("research_done");
        }
    };

    const apply = async () => {
        const selectedFields = {};
        diff.forEach((d) => {
            if (acceptField[d.field]) selectedFields[d.field] = d.proposed;
        });
        const selectedImages = Object.keys(acceptImages).filter((k) => acceptImages[k]);
        const hasExtras = saveExtras && Object.keys(extras || {}).some((k) => extras[k]);
        if (Object.keys(selectedFields).length === 0 && selectedImages.length === 0 && !hasExtras) {
            toast.error("Keine Auswahl getroffen — nichts zu übernehmen.");
            return;
        }
        setStage("applying");
        try {
            const r = await api.post(`/articles/${article.id}/ai-enrich/apply`, {
                fields: selectedFields,
                accept_image_paths: selectedImages,
                replace_images: replaceImages,
                extras: hasExtras ? extras : null,
            });
            toast.success(`KI-Übernahme erfolgreich: ${(r.data.fields_applied || []).join(", ")}`);
            onApplied?.(r.data.article);
            onClose?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Übernahme fehlgeschlagen");
            setStage("research_done");
        }
    };

    const renderValue = (v) => {
        if (v === null || v === undefined || v === "") return <span className="text-gray-400 italic">leer</span>;
        if (typeof v === "object") return <span className="font-mono-num text-[11px]">{JSON.stringify(v)}</span>;
        return String(v);
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="ai-enrich-modal">
            <div className="bg-white rounded-sm w-full max-w-5xl max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-[#111827] to-[#1F2937] text-white">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-[#FFCC00]" />
                        <h2 className="font-display text-lg font-bold">KI-Nachbearbeitung · {article.name}</h2>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setShowStorefront((v) => !v)}
                            className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${showStorefront ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "border-white/30 text-white/80 hover:bg-white/10"}`}
                            data-testid="ai-enrich-storefront-tab"
                        >
                            🛒 Shop-Vorschau
                        </button>
                        <button type="button" onClick={onClose} className="text-white/80 hover:text-white" data-testid="ai-enrich-close">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-5">
                    {showStorefront && (
                        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-sm p-4" data-testid="ai-enrich-storefront-preview">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">🛒 So sieht der Artikel im B2C-Shop aus:</div>
                            <div className="bg-white rounded-sm border border-gray-200 max-w-sm overflow-hidden">
                                <div className="aspect-square bg-gray-50 flex items-center justify-center text-gray-300">
                                    {(article.photo_paths || [])[0] ? (
                                        <PhotoThumb path={article.photo_paths[0]} alt={article.name} />
                                    ) : (
                                        <span className="text-xs">[kein Foto]</span>
                                    )}
                                </div>
                                <div className="p-3 space-y-1">
                                    <div className="font-bold text-sm line-clamp-2">{article.name}</div>
                                    <div className="text-[10px] text-gray-500 font-mono-num">SKU: {article.sku}</div>
                                    <div className="flex items-baseline justify-between mt-2">
                                        <div className="font-mono-num font-black text-lg">
                                            {previewPrice > 0 ? `${Number(previewPrice).toFixed(2)} €` : <span className="text-gray-400 text-sm">Auf Anfrage</span>}
                                        </div>
                                        <div className="text-[10px] text-gray-500">{inStock ? "Lieferbar" : "Anfragen"}</div>
                                    </div>
                                    {inStock && (article.stock_qty || 0) <= 3 && (
                                        <div className="text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-sm inline-block mt-1">
                                            🔥 Nur noch {article.stock_qty} verfügbar
                                        </div>
                                    )}
                                </div>
                            </div>
                            {!vis.visible_b2c && (
                                <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
                                    ⚠ Artikel ist <strong>nicht sichtbar</strong> im B2C-Shop. Setzen Sie unten den Haken bei „Sichtbar im B2C-Shop" und speichern.
                                </div>
                            )}
                        </div>
                    )}
                    {stage === "idle" && (
                        <div className="space-y-3">
                            <p className="text-sm text-gray-700">
                                Die KI durchsucht das Netz nach öffentlichen Daten zu diesem Artikel (Hersteller-Seite, Foren, Tests) und schlägt Ergänzungen vor. <strong>Nichts wird automatisch übernommen</strong> — Sie wählen pro Feld manuell.
                            </p>
                            <Button onClick={startResearch} className="rounded-sm bg-[#111827] hover:bg-black text-white" data-testid="ai-enrich-start-research">
                                <Sparkles className="w-4 h-4 mr-2" />
                                KI-Recherche starten
                            </Button>
                        </div>
                    )}

                    {stage === "researching" && (
                        <div className="text-center py-8" data-testid="ai-enrich-researching">
                            <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#FFCC00]" />
                            <div className="mt-3 text-sm text-gray-600">KI durchsucht das Netz nach Produkt-Daten…</div>
                            <div className="text-xs text-gray-400 mt-1">Das kann 15-45 Sekunden dauern.</div>
                        </div>
                    )}

                    {(stage === "research_done" || stage === "regen" || stage === "regen_done" || stage === "applying") && (
                        <>
                            {/* Felder-Diff */}
                            <div className="space-y-2" data-testid="ai-enrich-fields-diff">
                                <h3 className="font-bold text-sm uppercase tracking-wider text-gray-500">Feld-Vorschläge ({diff.length})</h3>
                                {diff.length === 0 && <div className="text-xs text-gray-500 italic">Keine Felder zur Übernahme — KI fand nichts Neues.</div>}
                                {diff.map((d) => (
                                    <div key={d.field} className={`border rounded-sm p-3 ${acceptField[d.field] ? "border-emerald-300 bg-emerald-50/40" : "border-gray-200 bg-white"}`} data-testid={`ai-enrich-diff-${d.field}`}>
                                        <label className="flex items-start gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={!!acceptField[d.field]}
                                                onChange={(e) => setAcceptField({ ...acceptField, [d.field]: e.target.checked })}
                                                className="mt-1 w-4 h-4 accent-emerald-600"
                                                data-testid={`ai-enrich-accept-${d.field}`}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">{d.field}</div>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                                                    <div className="bg-gray-50 rounded-sm p-2">
                                                        <div className="text-[10px] font-bold text-gray-500 mb-1">VORHER</div>
                                                        <div className="break-words">{renderValue(d.original)}</div>
                                                    </div>
                                                    <div className="bg-emerald-50 rounded-sm p-2 border border-emerald-200">
                                                        <div className="text-[10px] font-bold text-emerald-700 mb-1">KI-VORSCHLAG</div>
                                                        <div className="break-words">{renderValue(d.proposed)}</div>
                                                    </div>
                                                </div>
                                                {/* Jul-2026 (User-Wunsch) — bei Preisfeldern den EK zur Einordnung anzeigen */}
                                                {d.field === "list_price_gross" && (
                                                    <div className="mt-1.5 text-[11px] text-gray-600 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1" data-testid="ai-enrich-ek-hint">
                                                        Zur Einordnung: <strong>EK netto {fmtEur(article.purchase_price_net)}</strong>
                                                        {Number(article.purchase_price_gross) > 0 && <> · EK brutto {fmtEur(article.purchase_price_gross)}</>}
                                                        {Number(article.sell_price_gross) > 0 && <> · aktueller VK brutto {fmtEur(article.sell_price_gross)}</>}
                                                        {Number(d.proposed) > 0 && Number(article.purchase_price_net) > 0 && Number(d.proposed) < Number(article.purchase_price_net) && (
                                                            <span className="ml-1 font-bold text-red-700">⚠ Vorschlag liegt unter dem EK!</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </label>
                                    </div>
                                ))}
                            </div>

                            {/* Extras — Jul-2026 (User-Wunsch): am Artikel speicherbar */}
                            {(extras.technical_specs || extras.dimensions || extras.weight || extras.materials || extras.compatibility || extras.tags) && (
                                <details className="border border-gray-200 rounded-sm p-3 text-xs" data-testid="ai-enrich-extras" open>
                                    <summary className="font-bold cursor-pointer">Zusatz-Infos aus Recherche</summary>
                                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {extras.dimensions && <div><strong>Maße:</strong> {extras.dimensions}</div>}
                                        {extras.weight && <div><strong>Gewicht:</strong> {extras.weight}</div>}
                                        {extras.materials && <div><strong>Materialien:</strong> {extras.materials}</div>}
                                        {extras.tags?.length > 0 && <div className="col-span-2"><strong>Tags:</strong> {extras.tags.join(", ")}</div>}
                                        {extras.compatibility?.length > 0 && <div className="col-span-2"><strong>Kompatibel mit:</strong> {extras.compatibility.join(", ")}</div>}
                                        {extras.technical_specs && (
                                            <pre className="col-span-2 bg-gray-50 p-2 rounded-sm overflow-x-auto text-[11px]">{JSON.stringify(extras.technical_specs, null, 2)}</pre>
                                        )}
                                    </div>
                                    <label className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100 cursor-pointer" data-testid="ai-enrich-save-extras-label">
                                        <input type="checkbox" checked={saveExtras} onChange={(e) => setSaveExtras(e.target.checked)} className="w-4 h-4 accent-emerald-600" data-testid="ai-enrich-save-extras" />
                                        <span className="font-bold text-gray-800">Zusatz-Infos am Artikel speichern</span>
                                        <span className="text-gray-400">(Materialien, Tags, Kompatibilität — dauerhaft am Artikel sichtbar)</span>
                                    </label>
                                </details>
                            )}

                            {sources.length > 0 && (
                                <div className="text-[11px] text-gray-500" data-testid="ai-enrich-sources">
                                    <strong>Quellen:</strong> {sources.map((s, i) => (
                                        <a key={i} href={s} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5 ml-1">
                                            [{i + 1}]<ExternalLink className="w-2.5 h-2.5" />
                                        </a>
                                    ))}
                                </div>
                            )}

                            {/* Bilder-Re-Render */}
                            <div className="border-t border-gray-200 pt-4 space-y-3">
                                <h3 className="font-bold text-sm uppercase tracking-wider text-gray-500 flex items-center gap-2">
                                    <ImagePlus className="w-4 h-4 text-[#FFCC00]" />
                                    Produktbilder (Netz-Suche &amp; Nano Banana)
                                </h3>
                                {stage === "research_done" && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {/* Jul-2026 (User-Wunsch) — echte Bilder im Netz suchen */}
                                        <Button onClick={startWebImages} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs" data-testid="ai-enrich-web-images">
                                            <Globe className="w-3.5 h-3.5 mr-1" />
                                            Passende Bilder im Netz suchen
                                        </Button>
                                        <span className="text-[10px] text-gray-400">oder</span>
                                        <label className="text-xs text-gray-600">Anzahl Varianten:</label>
                                        <select
                                            value={regenCount}
                                            onChange={(e) => setRegenCount(Number(e.target.value))}
                                            className="rounded-sm border border-gray-300 h-8 px-2 text-xs"
                                            data-testid="ai-enrich-regen-count"
                                        >
                                            {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
                                        </select>
                                        <Button onClick={startRegen} className="rounded-sm bg-[#111827] hover:bg-black text-white h-8 text-xs" data-testid="ai-enrich-start-regen">
                                            <Sparkles className="w-3.5 h-3.5 mr-1" />
                                            Bilder rendern
                                        </Button>
                                    </div>
                                )}
                                {stage === "regen" && (
                                    <div className="text-center py-6">
                                        <Loader2 className="w-7 h-7 mx-auto animate-spin text-[#FFCC00]" />
                                        <div className="mt-2 text-xs text-gray-600">Suche/Rendere Produktbilder — kann 30-90 s dauern.</div>
                                    </div>
                                )}
                                {(stage === "regen_done" || stage === "applying") && proposals.length > 0 && (
                                    <div data-testid="ai-enrich-image-proposals">
                                        {/* Before/After Side-by-Side: Referenz + Varianten */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {/* Referenz (Vorher) */}
                                            {referencePath && (
                                                <div className="border-2 border-gray-400 rounded-sm overflow-hidden" data-testid="ai-enrich-reference">
                                                    <div className="aspect-square">
                                                        <PhotoThumb path={referencePath} alt="Referenz (Vorher)" />
                                                    </div>
                                                    <div className="bg-gray-100 text-[10px] font-bold uppercase tracking-wider text-gray-700 px-2 py-1 text-center">
                                                        ◀ Vorher (Referenz)
                                                    </div>
                                                </div>
                                            )}
                                            {proposals.map((p, idx) => (
                                                <div key={p.path} className={`border-2 rounded-sm overflow-hidden cursor-pointer ${acceptImages[p.path] ? "border-emerald-500" : "border-gray-200"}`}
                                                     onClick={() => setAcceptImages({ ...acceptImages, [p.path]: !acceptImages[p.path] })}
                                                     data-testid={`ai-enrich-proposal-${idx}`}>
                                                    <div className="aspect-square relative">
                                                        <PhotoThumb path={p.path} alt={`Vorschlag ${idx + 1}`} />
                                                        <div className={`absolute top-1 right-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${p.is_web_sourced ? "bg-emerald-600 text-white" : "bg-[#FFCC00] text-[#111827]"}`}>
                                                            {p.is_web_sourced ? "🌐 Web" : "✨ KI"}
                                                        </div>
                                                        {acceptImages[p.path] && (
                                                            <div className="absolute inset-0 bg-emerald-500/20 flex items-center justify-center">
                                                                <div className="bg-emerald-600 text-white rounded-full w-10 h-10 flex items-center justify-center">
                                                                    <Check className="w-6 h-6" />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="bg-emerald-50 text-[10px] font-bold uppercase tracking-wider text-emerald-700 px-2 py-1 text-center truncate">
                                                        {p.is_web_sourced
                                                            ? (p.source_url ? new URL(p.source_url).hostname.replace("www.", "") : "Web-Fund")
                                                            : `Nachher · Variante ${idx + 1}`}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <label className="flex items-center gap-2 mt-3 text-xs text-gray-600">
                                            <input
                                                type="checkbox"
                                                checked={replaceImages}
                                                onChange={(e) => setReplaceImages(e.target.checked)}
                                                className="w-4 h-4 accent-[#111827]"
                                                data-testid="ai-enrich-replace-images"
                                            />
                                            Bestehende Artikel-Fotos vollständig ersetzen (statt vorne anzufügen)
                                        </label>
                                        {proposals.some((p) => p.is_web_sourced) && (
                                            <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 mt-2" data-testid="ai-enrich-web-rights-hint">
                                                ⚠ Web-Funde können urheberrechtlich geschützt sein — bitte Nutzungsrechte prüfen, bevor sie im Shop veröffentlicht werden.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-4 flex items-center justify-between gap-2 bg-gray-50 flex-wrap">
                    {/* Sichtbarkeits-Block (Phase 4e.E) */}
                    <details className="w-full mb-2" data-testid="ai-enrich-visibility-section">
                        <summary className="font-bold text-sm cursor-pointer text-[#111827]">🛒 Sichtbarkeit & Preise</summary>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={vis.visible_b2c} onChange={(e) => setVis({ ...vis, visible_b2c: e.target.checked })} className="w-4 h-4 accent-emerald-600" data-testid="visibility-b2c" />
                                Sichtbar im B2C-Shop
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={vis.visible_b2b} onChange={(e) => setVis({ ...vis, visible_b2b: e.target.checked })} className="w-4 h-4 accent-blue-600" data-testid="visibility-b2b" />
                                Sichtbar für B2B-Händler
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={vis.internal_only} onChange={(e) => setVis({ ...vis, internal_only: e.target.checked })} className="w-4 h-4 accent-gray-600" data-testid="visibility-internal" />
                                Nur intern
                            </label>
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">B2C-Preis (brutto €)</label>
                                <input type="number" step="0.01" value={vis.price_b2c_gross} onChange={(e) => setVis({ ...vis, price_b2c_gross: e.target.value })} className="w-full rounded-sm border border-gray-300 px-2 py-1 mt-1 font-mono-num text-sm" data-testid="price-b2c" />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">B2B-Preis (netto €)</label>
                                <input type="number" step="0.01" value={vis.price_b2b_net} onChange={(e) => setVis({ ...vis, price_b2b_net: e.target.value })} className="w-full rounded-sm border border-gray-300 px-2 py-1 mt-1 font-mono-num text-sm" data-testid="price-b2b" />
                            </div>
                            <div className="flex items-end">
                                <Button onClick={saveVisibility} disabled={savingVis} className="rounded-sm bg-[#111827] hover:bg-black text-white w-full h-8 text-xs" data-testid="visibility-save">
                                    {savingVis ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Speichern"}
                                </Button>
                            </div>
                        </div>
                    </details>
                    <div className="ml-auto flex items-center gap-2">
                        <span className="text-[10px] text-gray-500 hidden md:inline" data-testid="ai-enrich-shortcut-hint">
                            ⌨ <kbd className="font-mono-num bg-white border border-gray-300 px-1 rounded-sm">A</kbd> alle akzeptieren · <kbd className="font-mono-num bg-white border border-gray-300 px-1 rounded-sm">R</kbd> alle verwerfen · <kbd className="font-mono-num bg-white border border-gray-300 px-1 rounded-sm">Enter</kbd> übernehmen · <kbd className="font-mono-num bg-white border border-gray-300 px-1 rounded-sm">Esc</kbd> schließen
                        </span>
                        <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="ai-enrich-cancel">
                            Schließen
                        </Button>
                        {(stage === "research_done" || stage === "regen_done") && (
                            <Button
                                onClick={apply}
                                disabled={stage === "applying"}
                                className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid="ai-enrich-apply-btn"
                            >
                                {stage === "applying" ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Übernehme…</> : <><Check className="w-4 h-4 mr-2" />Auswahl übernehmen</>}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
